import { useEffect, useCallback } from 'react';
import { 
  generatePixelBaseScript, 
  getLandingPageType, 
  extractPriceValue 
} from '@/lib/metaPixel';

/**
 * MetaPixelTracker Component
 * 
 * Renders Meta Pixel tracking script and fires appropriate events
 * based on the landing page type.
 * 
 * Props:
 * - pixelId: Meta Pixel ID (string, e.g., "123456789012345")
 * - landingSlug: Landing page slug to determine event type
 * - briefData: Brief data containing price, product_name, etc.
 * - onFormSubmit: Optional callback for custom form submit handling
 */
const MetaPixelTracker = ({ 
  pixelId, 
  landingSlug, 
  briefData = {},
  onFormSubmit 
}) => {
  const pageType = getLandingPageType(landingSlug);
  const price = extractPriceValue(briefData?.price);
  const currency = 'EUR';

  // Initialize pixel and fire page-load events
  useEffect(() => {
    if (!pixelId || typeof window === 'undefined') return;

    // Check if fbq already exists
    if (window.fbq) {
      // Fire page-specific event
      firePageEvent();
      return;
    }

    // Load Meta Pixel SDK
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Wait for SDK to load, then fire page event
    const checkAndFire = setInterval(() => {
      if (window.fbq) {
        clearInterval(checkAndFire);
        firePageEvent();
      }
    }, 100);

    // Cleanup
    return () => {
      clearInterval(checkAndFire);
    };
  }, [pixelId]);

  // Fire page-specific event based on landing type
  const firePageEvent = useCallback(() => {
    if (!window.fbq) return;

    switch (pageType) {
      case 'VIEW_CONTENT':
        window.fbq('track', 'ViewContent', {
          content_name: briefData?.product_name || briefData?.productName || '',
          content_category: briefData?.niche || ''
        });
        break;

      case 'CHECKOUT':
        window.fbq('track', 'InitiateCheckout', {
          value: price,
          currency: currency
        });
        break;

      case 'PURCHASE':
        window.fbq('track', 'Purchase', {
          value: price,
          currency: currency
        });
        break;

      case 'LEAD':
        // Lead event fires on form submit, not page load
        // Set up form listeners
        setupFormTracking();
        break;

      default:
        // Default to ViewContent for unknown pages
        window.fbq('track', 'ViewContent');
    }
  }, [pageType, briefData, price, currency]);

  // Set up form tracking for Lead events
  const setupFormTracking = useCallback(() => {
    const forms = document.querySelectorAll('form');
    
    forms.forEach(form => {
      // Avoid duplicate listeners
      if (form.dataset.pixelTracking) return;
      form.dataset.pixelTracking = 'true';

      form.addEventListener('submit', (e) => {
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
        // Call custom handler if provided
        if (onFormSubmit) {
          onFormSubmit(e);
        }
      });
    });
  }, [onFormSubmit]);

  // Re-setup form tracking when page content changes (for SPAs)
  useEffect(() => {
    if (pageType === 'LEAD') {
      setupFormTracking();

      // Observer for dynamically added forms
      const observer = new MutationObserver(() => {
        setupFormTracking();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => observer.disconnect();
    }
  }, [pageType, setupFormTracking]);

  // Also add noscript fallback for SEO
  if (!pixelId) return null;

  return (
    <noscript>
      <img 
        height="1" 
        width="1" 
        style={{ display: 'none' }}
        src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
        alt=""
      />
    </noscript>
  );
};

/**
 * Hook for manually firing pixel events
 * Use this for custom events like button clicks
 */
export const useMetaPixel = (pixelId) => {
  const trackEvent = useCallback((eventName, params = {}) => {
    if (!pixelId || typeof window === 'undefined' || !window.fbq) {
      console.warn('Meta Pixel not initialized');
      return;
    }

    window.fbq('track', eventName, params);
  }, [pixelId]);

  const trackLead = useCallback(() => {
    trackEvent('Lead');
  }, [trackEvent]);

  const trackPurchase = useCallback((value, currency = 'EUR') => {
    trackEvent('Purchase', { value, currency });
  }, [trackEvent]);

  const trackInitiateCheckout = useCallback((value, currency = 'EUR') => {
    trackEvent('InitiateCheckout', { value, currency });
  }, [trackEvent]);

  const trackViewContent = useCallback((contentName, contentCategory) => {
    trackEvent('ViewContent', {
      content_name: contentName,
      content_category: contentCategory
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackLead,
    trackPurchase,
    trackInitiateCheckout,
    trackViewContent
  };
};

export default MetaPixelTracker;
